import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import config from "../../config";

const getOrganizationId = () => {
  try {
    const userState = JSON.parse(localStorage.getItem("userState"));
    return userState?.user?.organizationId || "";
  } catch (_error) {
    return "";
  }
};

const getStudentName = (student) =>
  [student?.firstName, student?.lastName === "." ? "" : student?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

const getParentName = (student) =>
  [
    student?.parent?.firstName,
    student?.parent?.lastName === "." ? "" : student?.parent?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

const getParentDisplay = (student) => {
  const parentName = student?.parentName || getParentName(student);
  const parentPhone =
    student?.parentPhone ||
    student?.parent?.phoneNumber ||
    student?.parent?.phone_number ||
    "";

  if (parentName && parentPhone) return `${parentName} - ${parentPhone}`;
  if (parentName) return parentName;
  if (parentPhone) return parentPhone;
  return student?.parentId || "N/A";
};

const normalizeStudent = (student, index) => ({
  id: student?.id || index,
  name: getStudentName(student) || "Student",
  studentId: student?.studentId || "N/A",
  grade:
    student?.grade && student?.section && student.section !== "."
      ? `${student.grade} - ${student.section}`
      : student?.grade || "N/A",
  classTeacher: student?.classTeacher || "N/A",
  pickupStopId: student?.pickupStopId || "N/A",
  dropStopId: student?.dropStopId || "N/A",
  pickupStopName: student?.pickupStop?.name || student?.pickupStopName || "",
  dropStopName: student?.dropStop?.name || student?.dropStopName || "",
  parent: getParentDisplay(student),
});

const getStopDisplayName = (stopNameById, stopId, fallbackName = "") => {
  if (fallbackName) return fallbackName;
  if (!stopId || stopId === "N/A") return "N/A";
  return stopNameById.get(String(stopId)) || stopId;
};

export default function StudentListTable({
  vehicleId,
  stops = [],
  t = (value) => value,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const stopNameById = useMemo(() => {
    const map = new Map();

    stops.forEach((stop) => {
      const stopName = stop?.name || stop?.stopName;
      if (!stopName) return;

      [
        stop?.id,
        stop?.stopId,
        stop?.stop_id,
        stop?.value,
        stop?.label === stopName ? null : stop?.label,
      ].forEach((stopId) => {
        if (stopId !== undefined && stopId !== null && stopId !== "") {
          map.set(String(stopId), stopName);
        }
      });
    });

    return map;
  }, [stops]);

  useEffect(() => {
    const fetchVehicleStudents = async () => {
      const organizationId = getOrganizationId();
      if (!vehicleId || !organizationId) {
        setStudents([]);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${config.baseURL}/vehicle/id`, {
          params: { vehicleId, organizationId },
          withCredentials: true,
        });

        const vehicleStudents = Array.isArray(response.data?.vehicle?.students)
          ? response.data.vehicle.students
          : [];

        setStudents(vehicleStudents.map(normalizeStudent));
      } catch (err) {
        console.error("Error fetching vehicle students:", err);
        setError(t("Failed to load students"));
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleStudents();
  }, [vehicleId, t]);

  const columns = useMemo(
    () => [
      t("Student"),
      t("Student ID"),
      t("Grade"),
      t("Class Teacher"),
      t("Pick-up Stop"),
      t("Drop Stop"),
      // t("Parent"),
    ],
    [t],
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 160,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #A5DCF3",
          borderRadius: "4px",
          backgroundColor: "#FFFFFF",
        }}
      >
        <CircularProgress size={24} sx={{ color: "#20A8E0" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
          border: "1px solid #F4C7C3",
          borderRadius: "4px",
          backgroundColor: "#FFF5F5",
          color: "#B42318",
          fontSize: 14,
        }}
      >
        {error}
      </Box>
    );
  }

  if (!students.length) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          border: "1px solid #A5DCF3",
          borderRadius: "4px",
          backgroundColor: "#FFFFFF",
          color: "#647490",
          fontWeight: 500,
        }}
      >
        {t("No students assigned to this vehicle")}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
        {students.map((student) => (
          <Box
            key={student.id}
            sx={{
              p: 2,
              backgroundColor: "#FFFFFF",
              border: "1px solid #A5DCF3",
              borderRadius: "8px",
              boxShadow: "0px 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              {/* <Avatar sx={{ width: 36, height: 36, bgcolor: "#20A8E0", fontSize: 15 }}>
                {student.name.charAt(0).toUpperCase()}
              </Avatar> */}
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#183059", fontSize: 15 }}>
                  {student.name}
                </Typography>
                <Typography sx={{ color: "#647490", fontSize: 12 }}>
                  {student.studentId} · {student.grade}
                </Typography>
              </Box>
            </Box>
            {[
              [t("Class Teacher"), student.classTeacher],
              [
                t("Pick-up Stop"),
                getStopDisplayName(
                  stopNameById,
                  student.pickupStopId,
                  student.pickupStopName,
                ),
              ],
              [
                t("Drop Stop"),
                getStopDisplayName(
                  stopNameById,
                  student.dropStopId,
                  student.dropStopName,
                ),
              ],
              // [t("Parent"), student.parent],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 0.4,
                  borderTop: "1px solid #EEF2F6",
                }}
              >
                <Typography sx={{ color: "#647490", fontSize: 13 }}>{label}</Typography>
                <Typography sx={{ color: "#333", fontSize: 13, textAlign: "right" }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "4px",
        border: "1px solid #A5DCF3",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
      }}
    >
      <TableContainer component={Paper} sx={{ boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#E8EAEE",
                    color: "#183059",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((student) => (
              <TableRow
                key={student.id}
                sx={{
                  "&:hover": { backgroundColor: "#F5FAFD" },
                  transition: "background-color 0.15s",
                }}
              >
                <TableCell sx={{ minWidth: 190 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {/* <Avatar sx={{ width: 30, height: 30, bgcolor: "#20A8E0", fontSize: 13 }}>
                      {student.name.charAt(0).toUpperCase()}
                    </Avatar> */}
                    <Typography sx={{ fontWeight: 600, color: "#183059", fontSize: 14 }}>
                      {student.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{student.studentId}</TableCell>
                <TableCell>
                  <Chip
                    label={student.grade}
                    size="small"
                    sx={{
                      height: 24,
                      backgroundColor: "#F4F6FA",
                      color: "#183059",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>{student.classTeacher}</TableCell>
                <TableCell>
                  {getStopDisplayName(
                    stopNameById,
                    student.pickupStopId,
                    student.pickupStopName,
                  )}
                </TableCell>
                <TableCell>
                  {getStopDisplayName(
                    stopNameById,
                    student.dropStopId,
                    student.dropStopName,
                  )}
                </TableCell>
                {/* <TableCell>{student.parent}</TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}